import { useState } from 'react'
import { Text } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { ALL_TASKS, type TaskInfo } from './hooks'

interface TaskTreeProps {
  tasks: TaskInfo[]
  selectedSlug: string
  onSelect: (slug: string) => void
}

interface SlugGroup {
  name: string
  tasks: TaskInfo[]
}

/** Group slugs by their '.' namespace: 'cpg.sync-uspstf' → group 'cpg'; flat slugs stay top-level. */
function groupTasks(tasks: TaskInfo[]): { flat: TaskInfo[]; groups: SlugGroup[] } {
  const flat: TaskInfo[] = []
  const byGroup = new Map<string, TaskInfo[]>()
  for (const task of tasks) {
    const dot = task.slug.indexOf('.')
    if (dot === -1) {
      flat.push(task)
    } else {
      const group = task.slug.slice(0, dot)
      byGroup.set(group, [...(byGroup.get(group) ?? []), task])
    }
  }
  return { flat, groups: [...byGroup.entries()].map(([name, tasks]) => ({ name, tasks })) }
}

function TreeRow({
  label,
  depth,
  selected,
  dimmed = false,
  chevron,
  onClick,
}: {
  label: string
  depth: number
  selected?: boolean
  dimmed?: boolean
  chevron?: 'open' | 'closed'
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-1 truncate rounded px-2 py-1 text-left text-sm hover:bg-gray-100 ${
        selected ? 'bg-gray-200 font-medium' : ''
      } ${dimmed ? 'text-gray-400' : ''}`}
      style={{ paddingLeft: `${8 + depth * 16}px` }}
    >
      {chevron === 'open' && <IconChevronDown size={14} className="shrink-0" />}
      {chevron === 'closed' && <IconChevronRight size={14} className="shrink-0" />}
      <span className="truncate">{label}</span>
    </button>
  )
}

export function TaskTree({ tasks, selectedSlug, onSelect }: TaskTreeProps) {
  const [allCollapsed, setAllCollapsed] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const { flat, groups } = groupTasks(tasks)

  const toggleGroup = (name: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })

  const taskRow = (task: TaskInfo, depth: number, label = task.slug) => (
    <TreeRow
      key={task.slug}
      label={label}
      depth={depth}
      selected={selectedSlug === task.slug}
      dimmed={task.status === 'disabled'}
      onClick={() => onSelect(task.slug)}
    />
  )

  return (
    <nav className="p-2">
      <div className="flex items-center">
        <button
          type="button"
          aria-label={allCollapsed ? 'Expand tasks' : 'Collapse tasks'}
          onClick={() => setAllCollapsed((collapsed) => !collapsed)}
          className="rounded p-1 hover:bg-gray-100"
        >
          {allCollapsed ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
        </button>
        <button
          type="button"
          onClick={() => onSelect(ALL_TASKS)}
          className={`flex-1 rounded px-2 py-1 text-left text-sm font-semibold hover:bg-gray-100 ${
            selectedSlug === ALL_TASKS ? 'bg-gray-200' : ''
          }`}
        >
          All
        </button>
      </div>
      {!allCollapsed && (
        <div className="mt-1">
          {tasks.length === 0 && (
            <Text size="xs" c="dimmed" className="px-2 py-1">
              No tasks registered
            </Text>
          )}
          {flat.map((task) => taskRow(task, 1))}
          {groups.map((group) => (
            <div key={group.name}>
              <TreeRow
                label={group.name}
                depth={1}
                chevron={collapsedGroups.has(group.name) ? 'closed' : 'open'}
                onClick={() => toggleGroup(group.name)}
              />
              {!collapsedGroups.has(group.name) &&
                group.tasks.map((task) =>
                  // Label without the shared "<group>." prefix to keep the tree narrow.
                  taskRow(task, 2, task.slug.slice(group.name.length + 1)),
                )}
            </div>
          ))}
        </div>
      )}
    </nav>
  )
}
